<script setup>
import Button from 'dashboard/components-next/button/Button.vue';
import WootLabel from 'dashboard/components-next/label/Label.vue';

import { useAutomationOperationsFormatters } from './composables/useAutomationOperationsFormatters';

defineProps({
  summary: { type: Object, default: () => ({}) },
  latestRuns: { type: Array, default: () => [] },
  isBackfillPreviewLoading: { type: Boolean, default: false },
  isCutoverPreviewLoading: { type: Boolean, default: false },
});

defineEmits(['backfillPreview', 'cutoverPreview']);

const backfillLimit = defineModel('backfillLimit', {
  type: Number,
  required: true,
});
const cutoverArtifactPaths = defineModel('cutoverArtifactPaths', {
  type: String,
  required: true,
});
const cutoverMinCycles = defineModel('cutoverMinCycles', {
  type: Number,
  required: true,
});

const { latestCadenceRunLabel } = useAutomationOperationsFormatters();
</script>

<template>
  <div
    class="mb-3 flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between"
  >
    <div class="flex min-w-0 flex-col gap-2">
      <div class="flex flex-wrap items-center gap-2">
        <span class="text-heading-3 text-n-slate-12">
          {{ $t('AUTOMATION.OPERATIONS.CADENCES.TITLE') }}
        </span>
        <WootLabel
          :label="
            $t('AUTOMATION.OPERATIONS.CADENCES.ACTIVE', {
              count: summary.active_enrollment_count || 0,
            })
          "
          color="teal"
          compact
        />
        <WootLabel
          :label="
            $t('AUTOMATION.OPERATIONS.CADENCES.PAUSED', {
              count: summary.paused_enrollment_count || 0,
            })
          "
          :color="summary.paused_enrollment_count ? 'amber' : 'slate'"
          compact
        />
        <WootLabel
          :label="
            $t('AUTOMATION.OPERATIONS.CADENCES.DUE', {
              count: summary.due_timer_count || 0,
            })
          "
          :color="summary.due_timer_count ? 'amber' : 'slate'"
          compact
        />
        <WootLabel
          :label="
            $t('AUTOMATION.OPERATIONS.CADENCES.OLD_VERSION', {
              count: summary.old_version_active_count || 0,
            })
          "
          :color="summary.old_version_active_count ? 'amber' : 'slate'"
          compact
        />
        <WootLabel
          :label="
            $t('AUTOMATION.OPERATIONS.CADENCES.CONFLICTS', {
              count: summary.conflict_count || 0,
            })
          "
          :color="summary.conflict_count ? 'ruby' : 'slate'"
          compact
        />
      </div>
      <div
        v-if="latestRuns.length"
        class="flex flex-wrap gap-x-3 gap-y-1 text-body-main text-n-slate-11"
      >
        <span v-for="run in latestRuns.slice(0, 2)" :key="run.id">
          {{ latestCadenceRunLabel(run) }}
        </span>
      </div>
    </div>

    <div class="grid min-w-0 gap-3 lg:grid-cols-2">
      <form
        class="grid min-w-0 gap-2 sm:grid-cols-[7rem_auto]"
        data-test="cadence-backfill-preview-form"
        @submit.prevent="$emit('backfillPreview')"
      >
        <label class="flex min-w-0 flex-col gap-1">
          <span class="text-label-small text-n-slate-11">
            {{ $t('AUTOMATION.OPERATIONS.CADENCES.BACKFILL_LIMIT') }}
          </span>
          <input
            v-model.number="backfillLimit"
            min="1"
            max="500"
            type="number"
            data-test="cadence-backfill-limit"
            class="h-8 rounded-md border border-n-weak bg-n-surface-1 px-2 text-body-main text-n-slate-12"
          />
        </label>
        <Button
          :label="$t('AUTOMATION.OPERATIONS.CADENCES.PREVIEW_BACKFILL')"
          data-test="cadence-backfill-preview-run"
          icon="i-lucide-scan-search"
          color="slate"
          variant="outline"
          size="sm"
          type="submit"
          :is-loading="isBackfillPreviewLoading"
        />
      </form>

      <form
        class="grid min-w-0 gap-2 sm:grid-cols-[minmax(10rem,1fr)_5rem_auto]"
        data-test="cadence-cutover-preview-form"
        @submit.prevent="$emit('cutoverPreview')"
      >
        <label class="flex min-w-0 flex-col gap-1">
          <span class="text-label-small text-n-slate-11">
            {{ $t('AUTOMATION.OPERATIONS.CADENCES.ARTIFACTS') }}
          </span>
          <input
            v-model="cutoverArtifactPaths"
            type="text"
            data-test="cadence-cutover-artifacts"
            class="h-8 rounded-md border border-n-weak bg-n-surface-1 px-2 text-body-main text-n-slate-12"
          />
        </label>
        <label class="flex min-w-0 flex-col gap-1">
          <span class="text-label-small text-n-slate-11">
            {{ $t('AUTOMATION.OPERATIONS.CADENCES.MIN_CYCLES') }}
          </span>
          <input
            v-model.number="cutoverMinCycles"
            min="1"
            max="30"
            type="number"
            data-test="cadence-cutover-cycles"
            class="h-8 rounded-md border border-n-weak bg-n-surface-1 px-2 text-body-main text-n-slate-12"
          />
        </label>
        <Button
          :label="$t('AUTOMATION.OPERATIONS.CADENCES.PREVIEW_CUTOVER')"
          data-test="cadence-cutover-preview-run"
          icon="i-lucide-git-compare-arrows"
          color="slate"
          variant="outline"
          size="sm"
          type="submit"
          :is-loading="isCutoverPreviewLoading"
        />
      </form>
    </div>
  </div>
</template>
